/**
 * useDealsListViewPreference — Persiste a preferência de visualização
 * (lista x agrupada), o campo de agrupamento e o estado expandido de cada
 * grupo da listagem de Negócios em `localStorage`.
 *
 * Espelho fiel de `useTasksListViewPreference` (módulo de Tarefas), apenas
 * com chaves e valores próprios do CRM — nenhuma regra nova.
 *
 * Chaves utilizadas:
 *   - `deals:list:view-mode`               → 'list' | 'grouped'
 *   - `deals:list:group-by`                → DealGroupBy
 *   - `deals:list:groups-default-expanded` → 'collapsed' | 'expanded'
 *   - `deals:list:groups-expanded:<scope>` → boolean por grupo
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { DealGroupBy } from '@/components/crm/deals/list/DealGroupedView';

export type DealListViewMode = 'list' | 'grouped';
export type DealGroupsDefault = 'collapsed' | 'expanded';

const STORAGE_KEY_VIEW_MODE = 'deals:list:view-mode';
const STORAGE_KEY_GROUP_BY = 'deals:list:group-by';
const STORAGE_KEY_GROUPS_DEFAULT = 'deals:list:groups-default-expanded';

const VALID_VIEW_MODES: DealListViewMode[] = ['list', 'grouped'];
const VALID_GROUP_BY: DealGroupBy[] = [
  'status',
  'seller_id',
  'workspace_id',
  'source',
  'creation_origin',
  'probability',
  'pipeline_id',
];
const VALID_GROUPS_DEFAULT: DealGroupsDefault[] = ['collapsed', 'expanded'];

/** Lê valor do localStorage com fallback seguro. */
function readStorage<T extends string>(key: string, valid: T[], fallback: T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    if (raw && (valid as string[]).includes(raw)) return raw as T;
  } catch {
    /* ignora SSR/quota/privacy mode */
  }
  return fallback;
}

/** Grava valor no localStorage de forma silenciosa. */
function writeStorage(key: string, value: string) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    /* ignora SSR/quota */
  }
}

export function useDealsListViewPreference(
  defaults: {
    viewMode?: DealListViewMode;
    groupBy?: DealGroupBy;
    groupsDefault?: DealGroupsDefault;
  } = {}
) {
  const defaultViewMode: DealListViewMode = defaults.viewMode ?? 'list';
  const defaultGroupBy: DealGroupBy = defaults.groupBy ?? 'status';
  const defaultGroupsDefault: DealGroupsDefault = defaults.groupsDefault ?? 'collapsed';

  const [viewMode, setViewMode] = useState<DealListViewMode>(() =>
    readStorage(STORAGE_KEY_VIEW_MODE, VALID_VIEW_MODES, defaultViewMode)
  );
  const [groupBy, setGroupBy] = useState<DealGroupBy>(() =>
    readStorage(STORAGE_KEY_GROUP_BY, VALID_GROUP_BY, defaultGroupBy)
  );
  const [groupsDefault, setGroupsDefault] = useState<DealGroupsDefault>(() =>
    readStorage(STORAGE_KEY_GROUPS_DEFAULT, VALID_GROUPS_DEFAULT, defaultGroupsDefault)
  );

  useEffect(() => {
    writeStorage(STORAGE_KEY_VIEW_MODE, viewMode);
  }, [viewMode]);

  useEffect(() => {
    writeStorage(STORAGE_KEY_GROUP_BY, groupBy);
  }, [groupBy]);

  useEffect(() => {
    writeStorage(STORAGE_KEY_GROUPS_DEFAULT, groupsDefault);
  }, [groupsDefault]);

  return { viewMode, setViewMode, groupBy, setGroupBy, groupsDefault, setGroupsDefault };
}

/* =========================================================================
 * useDealGroupExpandedState — Persiste, por escopo, se um grupo da listagem
 * agrupada de Negócios está expandido.
 * ========================================================================= */
const STORAGE_KEY_GROUP_EXPANDED_PREFIX = 'deals:list:groups-expanded:';

export function useDealGroupExpandedState(
  scope: string,
  fallback: boolean
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const storageKey = STORAGE_KEY_GROUP_EXPANDED_PREFIX + scope;

  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return fallback;
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      /* ignora SSR/quota */
    }
    return fallback;
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(storageKey, expanded ? 'true' : 'false');
    } catch {
      /* ignora SSR/quota */
    }
  }, [storageKey, expanded]);

  return [expanded, setExpanded];
}
